import { useAction, useAtom } from "@reatom/npm-react";
import { useMemo, useEffect, useState, useCallback, useRef } from "react";
import { getProductSamplesRequest } from "src/pages/ProductPage/widgets/ProductSamples/store/fetchers";

import {
  getExportJobsRequest,
  EXPORT_JOBS_PAGE_SIZE,
} from "../../store/fetchers";

export const useExportTaskWidget = (taskId: string | undefined) => {
  const getExportJobs = useAction(getExportJobsRequest);
  const getProductSamples = useAction(getProductSamplesRequest);

  const [jobs] = useAtom(getExportJobsRequest.dataAtom);
  const [isJobsLoading] = useAtom(getExportJobsRequest.isLoadingAtom);

  const [page, setPage] = useState(1);
  const pageRef = useRef(page);

  const handlePageChange = useCallback(
    (newPage: number) => {
      pageRef.current = newPage;
      setPage(newPage);
      if (taskId) {
        getExportJobs({ id: taskId, page: newPage });
      }
    },
    [taskId, getExportJobs],
  );

  const [prevTaskId, setPrevTaskId] = useState(taskId);
  if (taskId !== prevTaskId) {
    setPrevTaskId(taskId);
    setPage(1);
  }

  // pageRef зеркалит page для опроса; ref нельзя писать в фазе рендера, поэтому
  // сбрасываем его на смену задачи здесь — эффект объявлен до первичной загрузки,
  // значит отработает раньше неё и она прочитает уже pageRef.current === 1.
  useEffect(() => {
    pageRef.current = 1;
  }, [taskId]);

  // Опрос имеет смысл только пока есть, что наблюдать — задачи в active/waiting
  // ещё крутятся и их прогресс реально тикает. Терминальные статусы
  // (completed/failed/stopped) больше не меняются, а delayed — это cron-родитель,
  // он "ждёт" следующего тика расписания, и 10-секундный опрос за ним —
  // пустая нагрузка.
  const hasLiveJob = useMemo(
    () =>
      jobs.data?.some((j) => j.status === "active" || j.status === "waiting") ??
      false,
    [jobs.data],
  );

  // Первичная загрузка на mount / смену задачи. Без неё мы не узнаем, есть ли
  // активные jobs — а значит и решение про polling построить не на чем.
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- первичная загрузка jobs/samples по taskId (data fetch)
    if (!taskId) return;
    getExportJobs({ id: taskId, page: pageRef.current });
    getProductSamples();
  }, [getExportJobs, getProductSamples, taskId]);

  useEffect(() => {
    if (!taskId || !hasLiveJob) return;
    const intervalId = setInterval(() => {
      getExportJobs({ id: taskId, page: pageRef.current });
    }, 10000);
    return () => clearInterval(intervalId);
  }, [getExportJobs, taskId, hasLiveJob]);

  useEffect(() => {
    if (!isJobsLoading && jobs.totalItems > 0) {
      const lastPage = Math.ceil(jobs.totalItems / EXPORT_JOBS_PAGE_SIZE);
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- клампим страницу после прихода totalItems (jobs могли удалиться)
      if (page > lastPage) {
        handlePageChange(1);
      }
    }
  }, [isJobsLoading, jobs.totalItems, page, handlePageChange]);

  return {
    jobs,
    isJobsLoading,
    page,
    pageSize: EXPORT_JOBS_PAGE_SIZE,
    onPageChange: handlePageChange,
  };
};
